/*
  Ideas :
    1. Mock data - add one more parameter to api function call (or convert it to object) which acts as a flag for mock data. e.g. 'mock = false' as default. If mock is required then return data from a list of mocked data for a specific request.
*/

import Axios from 'axios';
import axiosRetry from 'axios-retry';
import jwtDecode from 'jwt-decode';
import getEncryptedApiKey from './getEncryptedApiKey';

const axiosClient = Axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  // onUploadProgress: (progressEvent) => { // TODO usable for uploading images or gigger files. Maybe contact import file upload progres on modal submit button
  //   console.log(progressEvent);
  // },
});

// DO not use retry anymore - 1.9.2022.
// axiosRetry(axiosClient, {
//   retries: (process.env.NODE_ENV === 'production') ? 10 : 0,
//   retryDelay: () => 1000,
//   onRetry: (retryCount, error, requestConfig) => {
//     console.log('RETRY');
//     console.log(requestConfig);
//     // TODO If card saving request then do not retry... at the moment retry is off becase of this isssue
//     return;
//   },
// });

const requestsActive = {};

// Every request will reset this timeout to 50 minutes after which the simple request will be fired just to keep the user online status active.
let stayOnlineTimeout;

/**
 * When pagination next parameter is used it will reuse the original request parameters and then the same will be added. That is why all the previous unneeded parameters must be removed.
 * @param {String} url Url to be cleaned.
 * @returns Cleaned url from all the defined parameters.
 */
function cleanUrl(url) {
  return url.replace(/&_+=\d+/g, '');
}

export default function api(method, url, data, headers = null, apiPrefix = 'app') { // TODO once all the endpoints get translated, change this to 'w'
  url = cleanUrl(url);
  // TODO refactor this to only block endpoints that target an organisation e.g. 'w/organisation/a63e6863-1e61-457e-b286-a0a22ee67f7a/...'
  if (
    (!data || !data.forceApi) &&
    window.SBstore.getters['organisation/currentOrganisation'] &&
    (
      window.SBstore.getters['organisation/currentOrganisation'].should_activate_account ||
      window.SBstore.getters['organisation/currentOrganisation'].should_reactivate_account
    )
  ) {
    return Promise.reject(new Error(window.SBstore.getters['organisation/currentOrganisation'].should_activate_account ? 'Organisation has not been activated' : 'Organisation has been blocked'));
  }

  if (data && data.forceApi) {
    delete data.forceApi;
  }

  if (data && data.noPrefix) {
    delete data.noPrefix;
  } else {
    url = `${apiPrefix}/${url}`;
  }
  // This fixes double // in the url but ignores the :// format which is fine
  // https://dashboard-api.sendbee.io/w//organisation/123456789/contacts --> https://dashboard-api.sendbee.io/w/organisation/123456789/contacts
  // With this fix api calls are allowed with or without begining slash
  //    e.g. both of these are acceptable
  //         api('GET', `/organisation/123456789/contacts`, payload.data, null, 'w');
  //         api('GET', `organisation/123456789/contacts`, payload.data, null, 'w');
  url = url.replace(/([^:])(\/\/)/g, '$1/'); // TODO this should be converted to a interceptor to work with all the requests
  const token = localStorage.getItem('pbdToken');
  if (token && token.length) {
    const decodedToken = jwtDecode(token);
    const currentDate = Math.floor(Date.now() / 1000);
    if (currentDate >= decodedToken.exp) {
      localStorage.removeItem('pbdToken');
      window.location.reload();
    }
  }

  // Requests will cancel one another automatically unless there is a noCancel option in the data parameter.
  if (!(data && data.noCancel) && typeof requestsActive[url] !== typeof undefined) {
    // Abort previous request if it is already on the list
    requestsActive[url].abort();
  }
  const ac = new AbortController();
  if (data && data.noCancel) {
    delete data.noCancel;
    requestsActive[url] = ac;
  }

  const config = {
    withCredentials: true,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'X-Authorization': getEncryptedApiKey(),
      'Content-Type': 'application/json;charset=UTF-8',
      ...headers,
    },
    signal: ac.signal,
  };

  if (process.env.NODE_ENV === 'development') {
    const devHeader = process.env.VUE_APP_DEV_HEADER.split(':');
    config.headers[devHeader[0]] = devHeader[1];
  }

  if (token === null) {
    delete config.headers.Authorization;
  }

  let response;

  // TODO includeMe is confusing - it should be rewritten during rewrite to blockPusherReturnMessage, blockPusherResponse
  if (method !== 'GET' && data) {
    if (data instanceof FormData && !data.get('includeMe')) {
      data.set('socket_id', window.pusher.connection.socket_id);
      data.delete('includeMe');
    } else if (!(data instanceof FormData) && !data.includeMe && window.pusher) {
      // CS - https://pusher.com/docs/channels/server_api/excluding-event-recipients/#solution
      // User who initiates the change will be excluded from the Pusher event by default by adding the socket_id param to data but if there is a need for the event then add includeMe parameter to payload and no socket_id will be added to the request.
      data.socket_id = window.pusher.connection.socket_id;
      // just remove the includeMe from data if it is present
      delete data.includeMe;
    }
  }

  if (data && data.includeMe) {
    delete data.includeMe;
  }

  const me = window.SBstore.getters['organisation/me'];
  const overrideBaseURL = {};
  // Overrides base url if for some reason the provided url is a full url.
  // This is used in validators and in this case also a noPrefix option should be set to true.
  if (url.startsWith('http')) {
    overrideBaseURL.baseURL = '';
  }
  switch (method) {
    case 'GET':
      config.params = data || {};
      config.params._ = me.platform_id;
      response = axiosClient.get(url, { ...config, ...overrideBaseURL });
      break;
    case 'POST':
      response = axiosClient.post(url, data, config);
      break;
    case 'PUT':
      response = axiosClient.put(url, data, config);
      break;
    case 'DELETE':
      config.params = data;
      response = axiosClient.delete(url, config);
      break;
    case 'HEAD':
      response = axiosClient.head(url, { ...config, ...overrideBaseURL });
      break;
    default:
      return null;
  }

  if (window.sbgtag && window.sbgtag.event) {
    window.sbgtag.event('AJAX', {
      user: window.SBstore.getters['user/email'],
      method,
      url,
    });
  }

  response
    .then(() => {
      delete requestsActive[url];
    })
    .catch((error) => {
      // console.log('ERROR');
      // console.dir(error);
      if (error.response && error.response.status === 403) {
        // Now when we have all the permissions update via pusher this is not needed.
        // window.eventBus.$emit('403');
        if (error.response.data.type === 'permission_denied') {
          window.SBstore.dispatch('organisation/finalizeSplashView');
          window.SBstore.dispatch('user/permission_denied', error.response.data);
        } else if (
          // This is the case when the user token got invalid or deleted before it expires. In that case the user will be redirected to the login page and the local token will be deleted.
          // error.config.url === 'w/organisation/data' && // This is the only endpoint that is validated in the app and if it fails then the user will be redirected to the login page.
          error.response.data.type === 'authentication_failed' &&
          error.response.data.message === 'Unauthorized'
        ) {
          // console.log('RESETTTTTT');
          localStorage.removeItem('pbdToken');
          window.location.href = '/login';
        }
      } else if (!error.response || !error.response.data) {
        // const errorString = `errorName: ${error} , url: ${url}, method: ${method}, data: ${JSON.stringify(error)}`;
        // throw Error(errorString);
      } else if (error.response && error.response.status === 401) {
        localStorage.removeItem('pbdToken');
        window.location.href = '/login';
      }
    });

  clearTimeout(stayOnlineTimeout);
  stayOnlineTimeout = setTimeout(() => { window.SBstore.dispatch('system/fetchNewMessages'); }, 55 * 60 * 1000);

  return response;
}
